import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { login, youLoggedOut, youQuit } from "./profileSlice";
import { startGame, gameEnded, colorLocked } from "./gameSlice";
import { playerQuit } from "./playersSlice";

export enum DisplayMode {
  login = "login",
  splash = "splash",
  lobby = "lobby",
  ingame = "ingame",
  end = "end",
}

interface DisplayState {
  mode: DisplayMode;
  lockModal: {
    enabled: boolean;
    playerID: string | undefined;
    color: string | undefined;
  };
  disconnectModal: {
    enabled: boolean;
  };
}

const initialState: DisplayState = {
  mode: DisplayMode.splash,
  lockModal: {
    enabled: false,
    playerID: undefined,
    color: undefined,
  },
  disconnectModal: {
    enabled: false,
  },
};

const displaySlice = createSlice({
  name: "display",
  initialState,
  reducers: {
    setDisplayMode(state, action: PayloadAction<DisplayMode>) {
      state.mode = action.payload;
    },
    lockModalHidden(state) {
      state.lockModal.enabled = false;
      state.lockModal.playerID = undefined;
      state.lockModal.color = undefined;
    },
    disconnectModalEnabled(state) {
      state.disconnectModal.enabled = true;
    },
    disconnectModalDisabled(state) {
      state.disconnectModal.enabled = false;
    },
  },
  extraReducers(builder) {
    builder.addCase(startGame, (state) => {
      state.mode = DisplayMode.ingame;
    });
    builder.addCase(login, (state, action) => {
      state.mode =
        DisplayMode[action.payload.location as keyof typeof DisplayMode];
    });
    builder.addCase(gameEnded, (state) => {
      state.mode = DisplayMode.end;
      state.lockModal.enabled = false;
      state.lockModal.playerID = undefined;
      state.lockModal.color = undefined;
    });
    builder.addCase(youQuit, (state) => {
      state.mode = DisplayMode.lobby;
    });
    builder.addCase(colorLocked, (state, action) => {
      state.lockModal.enabled = true;
      state.lockModal.color = action.payload.color;
      state.lockModal.playerID = action.payload.player;
    });
    builder.addCase(youLoggedOut, (state) => {
      state.mode = DisplayMode.login;
    });
  },
});

export const {
  setDisplayMode,
  lockModalHidden,
  disconnectModalEnabled,
  disconnectModalDisabled,
} = displaySlice.actions;
export const displayReducer = displaySlice.reducer;
